import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { OnboardingData, PartnerInviteContext } from "./types";
import {
  saveOnboardingData,
  loadOnboardingData,
  clearOnboardingData,
} from "./onboardingStorage";
import { clearPartnerInvite as clearStoredPartnerInvite } from "./partnerInviteStorage";
import { getCurrentOnboardingFixtureScenario } from "./onboardingFixture";
import { sanitizeHydratedOnboardingData } from "./onboardingResume";

type OnboardingContextValue = {
  data: OnboardingData;
  updateField: <K extends keyof OnboardingData>(
    key: K,
    value: OnboardingData[K],
  ) => void;
  clearData: () => Promise<void>;
  beginPartnerInvite: (invite: PartnerInviteContext) => void;
  clearPartnerInvite: () => void;
  isPartnerInviteFlowActive: boolean;
  /** Begin a path from Welcome; resets prior answers on an intent change (#1127). */
  startPath: (intent: OnboardingData["primaryIntent"]) => void;
  isHydrated: boolean;
  onBackOverride: React.MutableRefObject<(() => void) | null>;
  setOnBackOverride: (handler: (() => void) | null) => void;
};

/**
 * Begin an onboarding path from the Welcome fork (#1127). Choosing a
 * different primary intent than the stored one starts fresh: every prior
 * answer is discarded so chips/defaults from an abandoned path can't
 * contaminate the new one (or the patient record it eventually PATCHes).
 * Re-selecting the same intent resumes the in-progress path, so Back
 * navigation doesn't wipe answers. Partner-invite context is activated only
 * by the explicit invite entry and is cleared when a normal path starts.
 */
export function resolvePathStart(
  prev: OnboardingData,
  intent: OnboardingData["primaryIntent"],
  defaults: OnboardingData,
): OnboardingData {
  const isPathChange = prev.primaryIntent !== intent;
  const base = isPathChange ? defaults : prev;
  return { ...base, primaryIntent: intent, partnerInvite: null };
}

export const defaultOnboardingData: OnboardingData = {
  primaryIntent: null,
  treatmentIntent: null,
  fertilityStage: null,
  plannedTreatmentModality: null,
  currentTreatmentFamily: null,
  periodRegularity: null,
  journeyRole: null,
  gender: null,
  dateOfBirth: null,
  age: null,
  firstName: null,
  ethnicity: [],
  conditions: [],
  conditionOther: null,
  partnerChoice: null,
  timeline: null,
  country: null,
  zipCode: null,
  hasEmployerBenefits: null,
  employerBenefit: null,
  partnerInvite: null,
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [data, setData] = useState<OnboardingData>(defaultOnboardingData);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isPartnerInviteFlowActive, setIsPartnerInviteFlowActive] =
    useState(false);
  const onBackOverride = useRef<(() => void) | null>(null);

  useEffect(() => {
    const fixture = getCurrentOnboardingFixtureScenario();
    if (fixture) {
      setData({ ...defaultOnboardingData, ...fixture.data });
      setIsHydrated(true);
      return;
    }

    loadOnboardingData().then((stored) => {
      if (stored) {
        // A persisted invitation is not enough to enter partner mode. It can
        // be left behind by an interrupted invite flow and must never turn a
        // normal app URL into the Sex step on the next visit.
        setData(sanitizeHydratedOnboardingData(stored));
        if (stored.partnerInvite) void clearStoredPartnerInvite();
      }
      setIsHydrated(true);
    });
  }, []);

  const updateField = useCallback(
    <K extends keyof OnboardingData>(key: K, value: OnboardingData[K]) => {
      if (key === "partnerInvite") {
        setIsPartnerInviteFlowActive(value !== null);
      }
      setData((prev) => {
        const next = { ...prev, [key]: value };
        saveOnboardingData(next);
        return next;
      });
    },
    [],
  );

  const clearData = useCallback(async () => {
    setData(defaultOnboardingData);
    setIsPartnerInviteFlowActive(false);
    await clearOnboardingData();
  }, []);

  const beginPartnerInvite = useCallback((invite: PartnerInviteContext) => {
    setIsPartnerInviteFlowActive(true);
    setData((prev) => {
      const next = { ...prev, partnerInvite: invite };
      saveOnboardingData(next);
      return next;
    });
  }, []);

  const clearPartnerInvite = useCallback(() => {
    setIsPartnerInviteFlowActive(false);
    setData((prev) => {
      const next = { ...prev, partnerInvite: null };
      saveOnboardingData(next);
      return next;
    });
  }, []);

  const startPath = useCallback((intent: OnboardingData["primaryIntent"]) => {
    setIsPartnerInviteFlowActive(false);
    setData((prev) => {
      const next = resolvePathStart(prev, intent, defaultOnboardingData);
      saveOnboardingData(next);
      return next;
    });
  }, []);

  const setOnBackOverride = useCallback((handler: (() => void) | null) => {
    onBackOverride.current = handler;
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        data,
        updateField,
        clearData,
        beginPartnerInvite,
        clearPartnerInvite,
        isPartnerInviteFlowActive,
        startPath,
        isHydrated,
        onBackOverride,
        setOnBackOverride,
      }}
    >
      {isHydrated ? children : null}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return ctx;
}
